/**
 * Follow-Up List Component (Presentation)
 *
 * Displays a scrollable list of follow-up cards in the queue panel.
 */

import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { useCallFollowUpSearch } from '@/modules/call-follow-up';
import type { CallFollowUpModel, CallFollowUpSearchRequest } from '@/modules/call-follow-up';
import { useAuthStore } from '@/modules/auth/application/store';
import { useCurrentUser } from '@/modules/user/application/hooks/useUser';

import { FollowUpCard } from './FollowUpCard';
import { PhoneCallSummaryModal } from './PhoneCallSummaryModal';
import { convertFollowUpToQueueModel } from '../utils/followUpToQueueModel';

import type { QueueModel } from '../../domain/types';

export function FollowUpList() {
  // Current user for default caller filter
  const userFromStore = useAuthStore(state => state.user);
  const { data: currentUser } = useCurrentUser();
  const user = currentUser ?? userFromStore;
  const currentUserId = user?.authenticatedId ? Number(user.authenticatedId) : user?.id ? Number(user.id) : undefined;

  // Request for follow-ups in queue panel (limited results), filtered by current user
  const followUpRequest: CallFollowUpSearchRequest = useMemo(
    () => ({
      page: 1,
      per_page: 50,
      filters: currentUserId != null ? [{ column: 'caller_cid', values: [String(currentUserId)] }] : [],
      sorts: [{ column: 'time_start', order: 'desc' }],
    }),
    [currentUserId]
  );

  // Fetch follow-ups
  const { data: searchResponse, isLoading, error } = useCallFollowUpSearch(followUpRequest);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedQueueData, setSelectedQueueData] = useState<QueueModel | null>(null);

  const followUps = searchResponse?.items ?? [];

  // Handle card click - open phone call summary modal
  const handleCardClick = useCallback((followUp: CallFollowUpModel) => {
    const queueModel = convertFollowUpToQueueModel(followUp);
    setSelectedQueueData(queueModel);
    setModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedQueueData(null);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography variant="body2" color="error" textAlign="center">
          Error loading follow-ups
        </Typography>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {error instanceof Error ? error.message : 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (followUps.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No follow-ups available
        </Typography>
      </Box>
    );
  }

  // List of follow-up cards
  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {followUps.map(followUp => (
          <FollowUpCard key={followUp.queue_call_id} followUp={followUp} onClick={() => handleCardClick(followUp)} />
        ))}
      </Box>

      {/* Phone Call Summary Modal */}
      <PhoneCallSummaryModal open={modalOpen} onClose={handleModalClose} queueData={selectedQueueData} />
    </>
  );
}
